/**
 * Visualizzatore fattura elettronica con il foglio di stile ufficiale AdE.
 *
 * Il rendering XSLT avviene LATO SERVER con lxml (l'XSLTProcessor dei browser
 * non regge il foglio AdE): il backend restituisce l'HTML gia' pronto
 * (GET /fatture/{id}/xml/rendered). Qui lo mostriamo in un iframe sandbox
 * (senza script, isolamento CSS) e forniamo il download dell'XML originale.
 * Se il rendering fallisce (422/errore), degrada alla vista dati (XmlViewer).
 *
 * Importa UI solo da @floraltech/ui (regola 6.8).
 */
import { useEffect, useRef, useState } from 'react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@floraltech/ui';

import XmlViewer from './XmlViewer';

interface Props {
  fatturaId: string;
  year: number;
  tipoDocumento?: string;
}

const AdeXmlViewer: React.FC<Props> = ({ fatturaId, year }) => {
  const [html, setHtml] = useState<string | null>(null);
  const [stato, setStato] = useState<'loading' | 'ready' | 'error'>('loading');
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  });

  useEffect(() => {
    let annullato = false;
    setStato('loading');
    void (async () => {
      try {
        const res = await fetch(
          `/api/v1/fatture/${fatturaId}/xml/rendered?year=${year}`,
          { headers: authHeaders() },
        );
        if (!res.ok) throw new Error(`Errore ${res.status}`);
        const out = await res.text();
        if (annullato) return;
        setHtml(out);
        setStato('ready');
      } catch {
        if (annullato) return;
        setStato('error');
      }
    })();
    return () => {
      annullato = true;
    };
  }, [fatturaId, year]);

  // Scrive l'HTML reso nell'iframe sandbox e adatta l'altezza.
  useEffect(() => {
    if (stato !== 'ready' || !html) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
    const adatta = () => {
      try {
        const h = doc.documentElement?.scrollHeight || doc.body?.scrollHeight || 800;
        iframe.style.height = `${h + 24}px`;
      } catch {
        iframe.style.height = '1000px';
      }
    };
    adatta();
    const t = window.setTimeout(adatta, 300);
    return () => window.clearTimeout(t);
  }, [stato, html]);

  const scaricaXml = async () => {
    try {
      const res = await fetch(
        `/api/v1/fatture/${fatturaId}/xml/raw?year=${year}`,
        { headers: authHeaders() },
      );
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const xml = await res.text();
      const blob = new Blob([xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fattura_${fatturaId}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      /* download best-effort */
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Fattura elettronica (foglio di stile Agenzia delle Entrate)</CardTitle>
        <Button variant="outline" size="sm" onClick={() => void scaricaXml()}>
          Scarica XML
        </Button>
      </CardHeader>
      <CardContent>
        {stato === 'loading' && (
          <p className="text-sm text-muted-foreground">Caricamento fattura...</p>
        )}
        {stato === 'error' && (
          <div className="space-y-4">
            <p className="text-sm text-destructive">
              Impossibile applicare il foglio di stile AdE. Vista dati alternativa:
            </p>
            <XmlViewer fatturaId={fatturaId} year={year} />
          </div>
        )}
        {stato === 'ready' && (
          <iframe
            ref={iframeRef}
            title="Fattura elettronica"
            sandbox="allow-same-origin"
            className="w-full border-0"
            style={{ minHeight: '400px' }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default AdeXmlViewer;
